// Passkeys im Browser.
//
// Der Server erzeugt die Optionen und prüft die Antwort; hier passiert nur
// der Teil, der im Browser passieren muss: `navigator.credentials` aufrufen
// und das Ergebnis so zurückgeben, wie der Server es lesen kann. Die
// Umwandlung zwischen JSON und Bytes übernimmt der Browser selbst
// (`parseCreationOptionsFromJSON`, `toJSON`) — Browser ohne diese beiden
// Funktionen bekommen keinen Passkey-Knopf angeboten.
//
// Eine Zeremonie besteht aus zwei Anfragen: Optionen holen, dann die Antwort
// schicken. Beide Schritte gehören in denselben Klick, weil Safari
// `navigator.credentials` nur innerhalb einer Nutzergeste zulässt.

import { api, ApiError, type Me, type PasskeyChallenge } from "@/lib/api";

type CredentialJSON = Record<string, unknown>;

type PublicKeyCredentialStatic = typeof PublicKeyCredential & {
  parseCreationOptionsFromJSON?: (json: unknown) => PublicKeyCredentialCreationOptions;
  parseRequestOptionsFromJSON?: (json: unknown) => PublicKeyCredentialRequestOptions;
};

type CredentialWithJSON = PublicKeyCredential & { toJSON?: () => CredentialJSON };

function pkc(): PublicKeyCredentialStatic | null {
  if (typeof window === "undefined" || !("PublicKeyCredential" in window)) return null;
  return window.PublicKeyCredential as PublicKeyCredentialStatic;
}

/** Ob dieser Browser Passkeys so kann, wie wir sie brauchen. */
export function supportsPasskeys(): boolean {
  const p = pkc();
  return (
    !!p &&
    typeof p.parseCreationOptionsFromJSON === "function" &&
    typeof p.parseRequestOptionsFromJSON === "function" &&
    !!navigator.credentials
  );
}

/** Was der Browser meldet, in einen Satz übersetzt. */
export class PasskeyError extends Error {
  constructor(message: string, readonly cancelled = false) {
    super(message);
    this.name = "PasskeyError";
  }
}

function translate(err: unknown): PasskeyError {
  if (err instanceof PasskeyError) return err;
  if (err instanceof ApiError) return new PasskeyError(err.detail);
  if (err instanceof DOMException) {
    switch (err.name) {
      case "NotAllowedError":
        return new PasskeyError("Der Vorgang wurde abgebrochen oder es kam keine Antwort.", true);
      case "InvalidStateError":
        return new PasskeyError("Dieser Authenticator ist hier schon registriert.");
      case "SecurityError":
        return new PasskeyError(
          "Der Browser lehnt Passkeys für diese Adresse ab (Origin passt nicht zur Konfiguration).",
        );
      case "NotSupportedError":
        return new PasskeyError("Dieser Browser oder dieses Gerät unterstützt keine Passkeys.");
    }
    return new PasskeyError(err.message || "Der Passkey-Vorgang ist fehlgeschlagen.");
  }
  return new PasskeyError("Der Passkey-Vorgang ist fehlgeschlagen.");
}

function toJSON(cred: Credential | null): CredentialJSON {
  const c = cred as CredentialWithJSON | null;
  if (!c || typeof c.toJSON !== "function") {
    throw new PasskeyError("Der Browser hat keinen Passkey zurückgegeben.");
  }
  return c.toJSON();
}

/** Einen neuen Passkey erzeugen; die Antwort geht so an den Server. */
export async function createPasskey(options: unknown): Promise<CredentialJSON> {
  const p = pkc();
  if (!p?.parseCreationOptionsFromJSON) {
    throw new PasskeyError("Dieser Browser unterstützt keine Passkeys.");
  }
  try {
    const publicKey = p.parseCreationOptionsFromJSON(
      (options as { publicKey: unknown }).publicKey,
    );
    return toJSON(await navigator.credentials.create({ publicKey }));
  } catch (err) {
    throw translate(err);
  }
}

/** Mit einem vorhandenen Passkey unterschreiben. */
export async function getPasskey(options: unknown): Promise<CredentialJSON> {
  const p = pkc();
  if (!p?.parseRequestOptionsFromJSON) {
    throw new PasskeyError("Dieser Browser unterstützt keine Passkeys.");
  }
  try {
    const publicKey = p.parseRequestOptionsFromJSON(
      (options as { publicKey: unknown }).publicKey,
    );
    return toJSON(await navigator.credentials.get({ publicKey }));
  } catch (err) {
    throw translate(err);
  }
}

/**
 * Die ganze Zeremonie der Anmeldung ohne Passwort: Optionen holen,
 * unterschreiben, Antwort schicken. Wirft ApiError, wenn der Server ablehnt
 * (etwa `isPasskeysUnavailable`), und PasskeyError, wenn der Browser es tut.
 */
export async function passkeySignIn(): Promise<Me> {
  const challenge = await api<PasskeyChallenge<unknown>>("/api/admin/passkeys/login/options", {
    method: "POST",
    body: {},
  });
  const credential = await getPasskey(challenge.options);
  return api<Me>("/api/admin/passkeys/login", {
    method: "POST",
    body: { token: challenge.token, credential },
  });
}

/** Der Passkey statt des Codes an der Schranke. */
export async function passkeyVerify(): Promise<Me> {
  const challenge = await api<PasskeyChallenge<unknown>>("/api/admin/mfa/passkey/options", {
    method: "POST",
    body: {},
  });
  const credential = await getPasskey(challenge.options);
  return api<Me>("/api/admin/mfa/passkey/verify", {
    method: "POST",
    body: { token: challenge.token, credential },
  });
}
